/**
 * Tax Calculation Utilities
 *
 * Centralises the unsafe `settings.paymentSettings.appliedTaxRate` access
 * pattern so consumer components don't need `as unknown as` casts.
 *
 * The `Club` type from @lgc/database stores `settings` as a JSON column
 * whose inner shape is not fully typed. This module performs the cast in
 * one place and exposes a safe, typed helper.
 *
 * `getAppliedTaxRateOptional` and `calculateTax` were deleted with the D12
 * subscription quote work — clients render server-quoted money, they never
 * derive it, and a client-side tax calculator left on the shelf is an
 * invitation to the next person. Booking's PaymentDetailsStep is the one
 * surviving consumer of the rate itself.
 *
 * @module lib/utils/taxCalculation
 */

/**
 * Safely extract the applied tax rate from a club's settings JSON.
 *
 * @param club - Any object with an optional `settings` property (e.g. ClubWithTenant)
 * @returns Tax rate as a percentage (e.g. 8.5 for 8.5%), or 0 if not configured
 */
export function getAppliedTaxRate(club: { settings?: unknown } | null | undefined): number {
  if (!club?.settings) return 0;

  const settings = club.settings as {
    paymentSettings?: { appliedTaxRate?: unknown };
  };

  const rate = settings.paymentSettings?.appliedTaxRate;
  return typeof rate === 'number' && Number.isFinite(rate) ? rate : 0;
}
